import type { FC, ReactNode } from "react"
import React, { useMemo } from "react"
import {
  Stack,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Box,
  Circle,
  HStack,
  Link,
  Icon,
} from "@chakra-ui/react"

import type {
  TransactionSimulationV2,
  ActionsSecurityCheckMetadata,
  SimulationStatus,
} from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import type { CreateProposalFormValues } from "createProposal/types/form"
import ExternalLink from "ui/components/icons/ExternalLink"
import type { RewardRecipient } from "createProposal/action/TransferArbitrumErc20Action"
import { recipeReadableMap } from "createProposal/action/types"
import CircleCheck from "ui/components/icons/CircleCheck"
import CircleXMark from "ui/components/icons/CircleXMark"

type Action = {
  recipe?: ExecutableCallType
  rewardsRecipients?: RewardRecipient[]
  sellToken?: { symbol: string }
}

type MappedSimulation = {
  action: number
  type?: ExecutableCallType
  simulation: TransactionSimulationV2
}

type Props = {
  metadata: ActionsSecurityCheckMetadata | null
  simulations: TransactionSimulationV2[] | null
  values?: CreateProposalFormValues
  noThreatsDetected: boolean
  someSimulationFailed: boolean
}

const ImpactOverview: FC<Props> = ({
  metadata,
  simulations,
  values,
  noThreatsDetected,
  someSimulationFailed,
}) => {
  const threatAnalysisEvents =
    metadata?.threatAnalysis?.actionsData?.flatMap(
      (actionsData) =>
        actionsData?.events?.filter((event) => event.severity !== "None") ?? [],
    ) ?? null

  function mapSimulationsToActions(
    actions: Action[],
    simulations: TransactionSimulationV2[] | null | undefined,
  ): MappedSimulation[] | undefined {
    if (!simulations) return undefined

    const accumulatedArray = actions.reduce(
      (acc: MappedSimulation[], action, actionIndex) => {
        const makeBaseObject = (offset: number) => ({
          action: actionIndex,
          type: action.recipe,
          simulation: simulations[acc.length + offset],
        })

        switch (action.recipe) {
          case ExecutableCallType.Custom:
            return [...acc, makeBaseObject(0)]

          case ExecutableCallType.Swap:
            const objectsForSwap = action.sellToken?.symbol === "ETH" ? 4 : 3

            return [
              ...acc,
              ...Array.from({ length: objectsForSwap }, (_, offset) =>
                makeBaseObject(offset),
              ),
            ]

          case ExecutableCallType.Nativetransfer:
          case ExecutableCallType.Erc20transfer:
          case ExecutableCallType.Erc20transferarbitrum:
            const additionalObjectsCount =
              action.rewardsRecipients && action.rewardsRecipients.length > 0
                ? action.rewardsRecipients.length
                : 0

            return [
              ...acc,
              ...Array.from(
                { length: 1 + additionalObjectsCount },
                (_, offset) => makeBaseObject(offset),
              ),
            ]

          default:
            return [...acc, makeBaseObject(0)]
        }
      },
      [],
    )

    if (accumulatedArray.length !== simulations.length) return undefined

    return accumulatedArray
  }

  const mappedSimulations = useMemo(() => {
    if (values?.actions) {
      return mapSimulationsToActions(values.actions, simulations)
    }
  }, [values?.actions, simulations])

  const threatAnalysisHeaders = ["Event", "Description", "Severity"]
  const simulationHeaders = mappedSimulations
    ? ["Action", "Type", "Simulation"]
    : ["Action", "Simulation"]

  return (
    <Stack p={4} spacing={5}>
      <Stack spacing={2}>
        <Text
          color="#475467"
          fontSize="16px"
          fontWeight={700}
          lineHeight="20px"
        >
          Simulations
        </Text>
        <Text
          color="#475467"
          fontSize="14px"
          fontWeight={500}
          lineHeight="20px"
        >
          Detailed simulation results per proposal action provided by Tenderly
        </Text>
        <ImpactTable
          headers={simulationHeaders}
          isEmpty={
            !Boolean(mappedSimulations && mappedSimulations.length) &&
            !Boolean(simulations?.length)
          }
        >
          {mappedSimulations && mappedSimulations.length
            ? mappedSimulations.map((mappedSimulation, idx) => {
                return (
                  <Tr key={`mapped-simulations-${idx}`}>
                    <Td py={5} width="30%">
                      <Text
                        color="#344054"
                        fontSize="14px"
                        fontWeight={400}
                        lineHeight="20px"
                        whiteSpace="normal"
                      >
                        {mappedSimulation.action + 1}
                      </Text>
                    </Td>
                    <Td width="50%">
                      <Text
                        color="#475467"
                        fontSize="14px"
                        fontWeight={400}
                        lineHeight="20px"
                        whiteSpace="normal"
                      >
                        {mappedSimulation.type
                          ? recipeReadableMap[mappedSimulation.type]
                          : ""}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <SimulationLink
                        result={
                          mappedSimulation.simulation.result as SimulationStatus
                        }
                        url={mappedSimulation.simulation.publicURI}
                      />
                    </Td>
                  </Tr>
                )
              })
            : simulations?.map((simulation, idx) => {
                return (
                  <Tr key={`simulations-${idx}`}>
                    <Td py={5} width="30%">
                      <Text
                        color="#344054"
                        fontSize="14px"
                        fontWeight={400}
                        lineHeight="20px"
                        whiteSpace="normal"
                      >
                        {idx + 1}
                      </Text>
                    </Td>

                    <Td isNumeric>
                      <SimulationLink
                        result={simulation.result as SimulationStatus}
                        url={simulation.publicURI}
                      />
                    </Td>
                  </Tr>
                )
              })}
        </ImpactTable>
      </Stack>
      <Stack spacing={2}>
        <Text
          color="#475467"
          fontSize="16px"
          fontWeight={700}
          lineHeight="20px"
        >
          Threat analysis
        </Text>
        <Text
          color="#475467"
          fontSize="14px"
          fontWeight={500}
          lineHeight="20px"
        >
          A full risk analysis model of the proposal actions + risk severity
        </Text>
        {threatAnalysisEvents ? (
          <ImpactTable
            headers={threatAnalysisHeaders}
            isEmpty={!Boolean(threatAnalysisEvents.length)}
            noThreatsDetected={noThreatsDetected}
            simulationFailedMessage="Threats could not be detected because the proposal simulation failed"
            someSimulationFailed={someSimulationFailed}
          >
            {threatAnalysisEvents.map((event, idx) => {
              return (
                <Tr key={`threat-analysis-${idx}`}>
                  <Td py={5} width="30%">
                    <Text
                      color="#344054"
                      fontSize="14px"
                      fontWeight={500}
                      lineHeight="20px"
                      whiteSpace="normal"
                    >
                      {event.eventType}
                    </Text>
                  </Td>
                  <Td width="50%">
                    <Text
                      color="#475467"
                      fontSize="14px"
                      fontWeight={400}
                      lineHeight="20px"
                      whiteSpace="normal"
                    >
                      {event.description}
                    </Text>
                  </Td>
                  <Td isNumeric>
                    <RiskIndicator risk={event.severity} />
                  </Td>
                </Tr>
              )
            })}
          </ImpactTable>
        ) : (
          <ImpactTable isEmpty headers={threatAnalysisHeaders} />
        )}
      </Stack>
    </Stack>
  )
}

type RiskIndicatorProps = {
  risk: string
  ignoreStyling?: boolean
}

const riskColorMap: Record<RiskIndicatorProps["risk"], string> = {
  Low: "yellow.400",
  Medium: "orange.300",
  High: "red.500",
}

const RiskIndicator: React.FC<RiskIndicatorProps> = ({
  risk,
  ignoreStyling,
}) => {
  return (
    <Box alignItems="center" display="flex" justifyContent="end">
      <Circle bg={riskColorMap[risk]} mr="2" size="6px" />
      {ignoreStyling ? (
        <Text>{risk}</Text>
      ) : (
        <Text
          color="#475467"
          fontSize="14px"
          fontWeight={500}
          lineHeight="20px"
        >
          {risk}
        </Text>
      )}
    </Box>
  )
}

type SimulationLinkProps = {
  result: SimulationStatus
  url: string
}

const SimulationText: React.FC<SimulationLinkProps> = ({ result, url }) => {
  const hasValidUrl = url && url.trim() !== ""

  return (
    <HStack align="center" justify="end">
      <Text
        _hover={hasValidUrl ? { textDecoration: "underline" } : undefined}
        color={result === "success" ? "green.500" : "red.500"}
        fontSize="14px"
        fontWeight={500}
        lineHeight="20px"
      >
        <Icon
          as={result === "success" ? CircleCheck : CircleXMark}
          fill={result === "success" ? "green.500" : "red.500"}
          mr={0.5}
        />
        {result === "success" ? "Passed" : "Failed"}
      </Text>
      {hasValidUrl ? (
        <Icon as={ExternalLink} color="#98A2B3" h={2.5} w={2.5} />
      ) : null}
    </HStack>
  )
}

const SimulationLink: React.FC<SimulationLinkProps> = ({ result, url }) => {
  const hasValidUrl = url && url.trim() !== ""

  return (
    <>
      {hasValidUrl ? (
        <Link isExternal _hover={{ textDecoration: "none" }} href={url}>
          <SimulationText result={result} url={url} />
        </Link>
      ) : (
        <SimulationText result={result} url={url} />
      )}
    </>
  )
}

type ImpactHeaderProps = {
  headers: string[]
}

const ImpactHeader: FC<ImpactHeaderProps> = ({ headers }) => (
  <Thead>
    <Tr>
      {headers.map((header, idx) => (
        <Th
          key={`impact-header-${idx}`}
          isNumeric={idx === headers.length - 1}
          py={5}
        >
          <Text
            color="#475467"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
          >
            {header}
          </Text>
        </Th>
      ))}
    </Tr>
  </Thead>
)

type ImpactTableProps = {
  headers: string[]
  children?: ReactNode
  isEmpty?: boolean
  noThreatsDetected?: boolean
  someSimulationFailed?: boolean
  simulationFailedMessage?: string
  threatAnalysisAvailable?: boolean
}

const ImpactTable: FC<ImpactTableProps> = ({
  headers,
  children,
  isEmpty = false,
  noThreatsDetected = false,
  someSimulationFailed = false,
  threatAnalysisAvailable = true,
  simulationFailedMessage = "Unable to display data because the simulation failed",
}) => {
  return (
    <TableContainer bg="white" borderRadius="md" borderWidth="1px">
      <Table variant="simple">
        {!isEmpty ? <ImpactHeader headers={headers} /> : null}

        {isEmpty && (!noThreatsDetected || someSimulationFailed) ? (
          <Tbody>
            <Flex justify="left" px={6} py={4} w="full">
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                whiteSpace="normal"
              >
                {threatAnalysisAvailable === false
                  ? "Unavailable"
                  : someSimulationFailed
                  ? simulationFailedMessage
                  : "Unavailable"}
              </Text>
            </Flex>
          </Tbody>
        ) : noThreatsDetected ? (
          <Tbody>
            <Tr>
              <Flex justify="left" px={6} py={4} w="full">
                <Text
                  color="#475467"
                  fontSize="14px"
                  fontWeight={400}
                  lineHeight="20px"
                  whiteSpace="normal"
                >
                  <Icon as={CircleCheck} fill="green.500" mr={0.25} />
                  No threats detected
                </Text>
              </Flex>
            </Tr>
          </Tbody>
        ) : (
          <Tbody>{children}</Tbody>
        )}
      </Table>
    </TableContainer>
  )
}

export default ImpactOverview
